import { useMemo, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'store/store'
import {
  Table,
  Tooltip,
  Checkbox,
  Typography,
  Input,
  Modal,
  DSInputType,
  SlimButton,
  Pagination,
  LoadingHavaianas,
  NotFound,
  HeadItemType,
  Icon
} from 'components'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import { toAllFirstLetterUpper } from 'commons/utils/string'
import {
  getAnalysisItems,
  interruptAnalysis,
  setAnalysisRecords
} from 'store/SalesOrderContext/salesOrderSlice'
import { setSalesOrderCurrentTab } from 'store/StructuralContext/structuralSlice'
import { sumArray } from 'commons/utils/array'
import useDebounce from 'commons/customHooks/useDebounce'
import { usePermissions } from 'commons/customHooks/useAuth'
import { ActionPerm } from 'components/Structures/Header/constants'
import { SCREENS, ACTIONS_SCREENS } from 'commons/utils/permissions'

import * as S from './styles'
import { availableCentersFactories } from 'constants/enums'
import { theme } from 'styles/theme/hav-theme'

const AnalysisTab = () => {
  const dispatch = useDispatch()
  const {
    draftBody,
    analysisRecords,
    loadingMaterials: loading
  } = useSelector((state) => state.salesOrder)
  const [searchValue, setSearchValue] = useState('')
  const [openFilterModal, setOpenFilterModal] = useState(false)
  const [confirmInterruptModal, setConfirmInterruptModal] = useState(false)
  const pageSize = 20
  const getTotalPages = () => Math.ceil(getTotalItems() / pageSize)
  const getTotalItems = () => analysisRecords.length
  const [currentPage, setCurrentPage] = useState<number>(1)

  const factoryFilterOptions = availableCentersFactories
  const [selectedFactoryFilters, setSelectedFactoryFilters] = useState<string[]>([])
  const [appliedFactoryFilters, setAppliedFactoryFilters] = useState<string[]>([])

  const materialStatusOptions = ['SEM COTA', 'SEM ESTOQUE']
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<string[]>([])
  const [appliedStatusFilters, setAppliedStatusFilters] = useState<string[]>([])

  const { verifyPermission } = usePermissions()
  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.ORDER, action)
  }

  useEffect(() => {
    dispatch(getAnalysisItems())
  }, [])

  const dispatchSearch = (value: string) => {
    dispatch(getAnalysisItems(value, appliedFactoryFilters, appliedStatusFilters))
  }
  const debounceSearch = useDebounce(dispatchSearch, 1000)
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLocaleUpperCase()
    setSearchValue(value)
    debounceSearch(value)
  }

  const clearFilters = () => {
    setSelectedFactoryFilters([])
    setAppliedFactoryFilters([])
    setSelectedStatusFilters([])
    setAppliedStatusFilters([])
    setSearchValue('')
    dispatch(getAnalysisItems())
    setOpenFilterModal(false)
  }

  const sizesSet = useMemo(() => {
    const memoSet = new Set<string>()
    analysisRecords.forEach(({ tamanhos }) => {
      tamanhos.forEach(({ cd_tamh }) => {
        const t = { size: cd_tamh }
        memoSet.add(JSON.stringify(t))
      })
    })
    return memoSet
  }, [analysisRecords])

  const statusLabels = {
    'SEM COTA': 'Indisponível por cota',
    'SEM ESTOQUE': 'Indisponível por estoque'
  }
  const statusIcons = {
    'SEM COTA': <Icon icon="report" color={theme.colors.alert.warning.dark} size="15" />,
    'SEM ESTOQUE': <Icon icon="box" color={theme.colors.alert.warning.dark} size="15" />
  }

  const tableLabels = (): HeadItemType[] => {
    const sizes = [...sizesSet].sort().map((size) => {
      const object: { size: string } = JSON.parse(size)

      return { label: object.size, columnName: object.size }
    })

    return [
      { label: '', columnName: '' },
      { label: 'Material', columnName: 'Material' },
      { label: 'Descrição', columnName: 'DescricaoMaterial' },
      { label: 'Cor', columnName: 'Cor' },
      { label: 'Descrição', columnName: 'DescricaoCor' },
      { label: 'Fábrica', columnName: 'Fabrica' },
      { label: 'Qtd.', columnName: 'Qtd' },
      { label: 'Valor (R$)', columnName: 'Valor' },
      { label: 'Desconto (%)', columnName: 'Desconto' },
      ...sizes
    ]
  }

  const tableData = () => {
    const recordsData = analysisRecords
      .slice(pageSize * (currentPage - 1), pageSize * currentPage)
      .map((record, key) => {
        const sizes = [...sizesSet].sort().map((size) => {
          const object: { size: string } = JSON.parse(size)
          const foundSize = record.tamanhos.find((t) => t.cd_tamh === object.size)
          if (foundSize?.cd_tamh) {
            return record.cd_matr[0] === '7' // TODO: Replace magic number with constant
              ? numberToDecimalString(foundSize.qt_item)
              : numberToDecimalString(foundSize.qt_item_conv)
          }
          return <S.EmptyValue key={`${key}-${object.size}`}>0</S.EmptyValue>
        })

        return {
          id: `${record.cd_matr}-${record.cd_cor}-${record.nr_item_pedi_rasc}`,
          checked: !!record.checked,
          content: [
            record.ds_moti_crit ? (
              <Tooltip
                key={`icon-${record.cd_matr}-${record.cd_cor}`}
                title={statusLabels[record.ds_moti_crit as keyof typeof statusLabels]}
                placement="top"
                arrow
              >
                <S.IconSpan>
                  {statusIcons[record.ds_moti_crit as keyof typeof statusLabels]}
                </S.IconSpan>
              </Tooltip>
            ) : (
              ''
            ),
            record.cd_matr,
            toAllFirstLetterUpper(record.ds_matr),
            record.cd_cor,
            toAllFirstLetterUpper(record.ds_cor),
            record.cd_cent_matr ?? '--',
            numberToDecimalString(record.qt_tota_item_conv),
            record.vl_tota_item ? formatToPTBRNumber(record.vl_tota_item) : '-',
            record.pc_dsct ? `${record.pc_dsct}%` : <S.EmptyValue>0%</S.EmptyValue>,
            ...sizes
          ]
        }
      })
    return recordsData
  }

  const setMaterialCheck = (checked: boolean, id?: string) => {
    if (id) {
      const nr_item_pedi_rasc = id.split('-')[2]

      const newRecords = analysisRecords.map((record) => {
        if (record.nr_item_pedi_rasc === Number(nr_item_pedi_rasc)) {
          return {
            ...record,
            checked
          }
        }
        return record
      })
      dispatch(setAnalysisRecords(newRecords))
    } else {
      const newRecords = analysisRecords.map((record) => {
        return {
          ...record,
          checked
        }
      })
      dispatch(setAnalysisRecords(newRecords))
    }
  }

  const getCheckedRecords = () => {
    return analysisRecords.filter((record) => record.checked)
  }

  const calcBoxes = () => {
    const valuesToSum = analysisRecords.map((item) => {
      return item.qt_tota_item
    })
    return valuesToSum.reduce(sumArray, 0)
  }
  const calcPairs = () => {
    const valuesToSum = analysisRecords.map((item) => {
      return item.qt_tota_item_conv
    })
    return valuesToSum.reduce(sumArray, 0)
  }
  const calcSelectedPairs = () => {
    const valuesToSum = getCheckedRecords().map((item) => {
      return item.qt_tota_item_conv
    })
    return numberToDecimalString(valuesToSum.reduce(sumArray, 0))
  }
  const calcTotal = () => {
    const valuesToSum = analysisRecords.map((item) => item.vl_tota_item || 0)
    return valuesToSum.reduce(sumArray, 0)
  }

  const toggleFactoryFilterItem = (option: string) => {
    setSelectedFactoryFilters((prev) => {
      if (prev.includes(option)) {
        return prev.filter((item) => item !== option)
      }
      return [...prev, option]
    })
  }

  const toggleStatusFilterItem = (option: string) => {
    setSelectedStatusFilters((prev) => {
      if (prev.includes(option)) {
        return prev.filter((item) => item !== option)
      }
      return [...prev, option]
    })
  }

  const hasSearch = () =>
    searchValue.length || appliedFactoryFilters.length || appliedStatusFilters.length

  const appliedFiltersAmount = () => {
    let amount = 0
    if (appliedFactoryFilters.length > 0) amount++
    if (appliedStatusFilters.length > 0) amount++
    return amount
  }

  const handleInterruptAnalysis = () => {
    const checkedRecords = getCheckedRecords()
    const items = new Set<number>()
    checkedRecords.forEach((record) => {
      items.add(record.nr_item_pedi_rasc)
    })
    if (checkedRecords.length === analysisRecords.length) {
      dispatch(setSalesOrderCurrentTab(1))
    }
    dispatch(
      interruptAnalysis(
        [...items].map((item) => {
          return {
            nr_pedi_rasc: draftBody.nr_pedi_rasc,
            nr_item_pedi_rasc: item
          }
        })
      )
    )
    setConfirmInterruptModal(false)
  }

  return (
    <S.Wrapper>
      <S.HeadContainer>
        <S.TitleWrapper>
          <Typography design="hav-h5-text">Materiais pendentes por análise</Typography>
          <Typography design="hav-helper-text">
            Uma notificação será enviada assim que a análise for concluída e será possível
            prosseguir de acordo com o resultado.
          </Typography>
        </S.TitleWrapper>

        <S.SearchWrapper>
          <div>
            <Input
              width={165}
              label="Localizar material"
              value={searchValue}
              placeholder="Ex.: 4000029"
              addorment
              onChange={handleSearch}
              design={DSInputType.SEARCH_INPUT}
            />
          </div>
          <S.FilterButtonWrapper>
            <S.FilterButton onClick={() => setOpenFilterModal(true)} disabled={loading}>
              <Icon icon="filter" size="30" />
            </S.FilterButton>
            {appliedFiltersAmount() > 0 && (
              <S.FiltersNumberStyled>{appliedFiltersAmount()}</S.FiltersNumberStyled>
            )}
          </S.FilterButtonWrapper>
        </S.SearchWrapper>
      </S.HeadContainer>

      <S.Divider />
      {loading ? (
        <S.LoadingContainer>
          <LoadingHavaianas />
        </S.LoadingContainer>
      ) : (
        <>
          <S.ActionRow>
            <SlimButton
              content="Interromper análise"
              variant="secondary"
              onClick={() => setConfirmInterruptModal(true)}
              icon={<Icon icon="undo" size="15" />}
              disabled={
                !getCheckedRecords().length || shouldDisable(ACTIONS_SCREENS.EDIT)
              }
            />
            <Typography design="hav-default-03-text">
              {getCheckedRecords().length > 0
                ? `${calcSelectedPairs()} pares em análise selecionados`
                : 'Nenhum par em análise está selecionado'}
            </Typography>
          </S.ActionRow>

          {analysisRecords.length ? (
            <S.TableWrapper>
              <Table
                hasCheckbox
                checkboxSize="small"
                rowHeight={32}
                headData={tableLabels()}
                bodyData={tableData()}
                setChecked={setMaterialCheck}
                iconColumns={[0]}
              />
            </S.TableWrapper>
          ) : (
            <S.NotFoundWrapper>
              <NotFound
                title={'Nenhum resultado encontrado'}
                description={
                  <S.NotFoundContent>
                    {hasSearch() ? (
                      <>
                        Verifique os critérios utilizados e tente novamente.
                        <SlimButton
                          content="Reiniciar busca e filtros"
                          type="button"
                          icon={<Icon icon="chevron-left" size="15" />}
                          variant="white"
                          onClick={clearFilters}
                        />
                      </>
                    ) : (
                      'Nenhum material pendente por análise neste pedido.'
                    )}
                  </S.NotFoundContent>
                }
              />
            </S.NotFoundWrapper>
          )}

          <S.SummaryWrapper>
            <div>
              {getTotalItems() > pageSize && (
                <Pagination
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  numberOfPages={getTotalPages()}
                  numberOfItems={getTotalItems()}
                  itemsPerPage={pageSize}
                />
              )}
            </div>
            <S.SummaryRow>
              <S.SummaryItem>
                <Typography design="hav-default-02-text">Volume • pares</Typography>
                <span>{numberToDecimalString(calcPairs())}</span>
              </S.SummaryItem>
              <S.SummaryItem>
                <Typography design="hav-default-02-text">Volume • caixas</Typography>
                <span>{numberToDecimalString(calcBoxes())}</span>
              </S.SummaryItem>
              <S.SummaryItem>
                <Typography design="hav-default-02-text">Valor total</Typography>
                <span>{formatToPTBRNumber(calcTotal())}</span>
              </S.SummaryItem>
            </S.SummaryRow>
          </S.SummaryWrapper>
        </>
      )}

      <Modal
        open={openFilterModal}
        setOpen={setOpenFilterModal}
        closeAction={() => {
          setSelectedFactoryFilters(appliedFactoryFilters)
          setSelectedStatusFilters(appliedStatusFilters)
          setOpenFilterModal(false)
        }}
        showAction={false}
        title="Filtro de materiais"
        titleSize={22}
        modalWidth={450}
      >
        <S.FilterOption>
          <span>Fábrica</span>
          <S.CheckboxRow>
            {factoryFilterOptions.map((option) => (
              <Checkbox
                key={option}
                size="small"
                checked={selectedFactoryFilters.includes(option)}
                onChange={() => toggleFactoryFilterItem(option)}
                label={option}
              />
            ))}
          </S.CheckboxRow>
        </S.FilterOption>

        <S.FilterOption>
          <span>Status</span>
          <S.CheckboxList>
            {materialStatusOptions.map((option) => (
              <Checkbox
                key={option}
                size="small"
                checked={selectedStatusFilters.includes(option)}
                onChange={() => toggleStatusFilterItem(option)}
                label={
                  <S.IconSpan>
                    {statusIcons[option as keyof typeof statusLabels]}
                    {statusLabels[option as keyof typeof statusLabels]}
                  </S.IconSpan>
                }
              />
            ))}
          </S.CheckboxList>
        </S.FilterOption>

        <S.ModalActionsRow>
          <SlimButton
            variant="secondary"
            content="Limpar filtros"
            onClick={clearFilters}
          />
          <SlimButton
            variant="primary"
            content="Filtrar"
            onClick={() => {
              setAppliedFactoryFilters(selectedFactoryFilters)
              setAppliedStatusFilters(selectedStatusFilters)
              dispatch(
                getAnalysisItems(
                  searchValue,
                  selectedFactoryFilters,
                  selectedStatusFilters
                )
              )
              setOpenFilterModal(false)
            }}
          />
        </S.ModalActionsRow>
      </Modal>
      <Modal
        open={confirmInterruptModal}
        setOpen={setConfirmInterruptModal}
        closeAction={() => setConfirmInterruptModal(false)}
        showAction={false}
        title="Interromper análise"
        titleSize={22}
        description={`Seu material retornará para a aba “Em aberto” como um rascunho, permitindo que
        você edite ou adicione algumas configurações e quantidades.`}
        modalWidth={450}
      >
        <S.ModalActionsRow>
          <SlimButton
            variant="secondary"
            content="Voltar"
            onClick={() => setConfirmInterruptModal(false)}
          />
          <SlimButton
            variant="primary"
            content="Confirmar"
            onClick={handleInterruptAnalysis}
          />
        </S.ModalActionsRow>
      </Modal>
    </S.Wrapper>
  )
}

export default AnalysisTab
